import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import type { IScheduleBlockAvailable } from '@/types/schedule';

import { Button } from '@/components/common';
import { flex, media } from '@/styles/theme';
import { WarningTriangle } from '@/assets/icons';
import { formatDate } from '@/helpers/date';
import { formatStartEndTimeWithTimezone } from '@/helpers';
import { NAVBAR_HEIGHT, SHIFTPICKUP_CART_HEADER_HEIGHT, SHIFTPICKUP_CART_TOP_SPACE } from '@/config/variables';
import { useTimezone } from '@/hooks';
import { getBlockSettings, getDateFromBlock } from '@/helpers/block';

interface IPickedShiftsWithWarning {
  onSubmit: () => void;
  unpickedShiftsByDay: { [key: string]: IScheduleBlockAvailable[] };
}

const PickedShiftsWithWarning = ({ onSubmit, unpickedShiftsByDay }: IPickedShiftsWithWarning): React.ReactElement => {
  const { t } = useTranslation();
  const { timezoneName } = useTimezone();

  const formatShiftLabel = useCallback(
    (startBlock: number, endBlock: number) => {
      const position = getBlockSettings(startBlock, endBlock, timezoneName);
      return formatStartEndTimeWithTimezone(position);
    },
    [timezoneName],
  );

  return (
    <ContainerBody>
      <StyledIcon src={WarningTriangle} />
      {t`mySchedule.pickUpShiftCart.labelSomeShiftsWerentPicked`}
      <UnpickedShiftsContainer>
        {Object.entries(unpickedShiftsByDay).map(([key, shifts]) => (
          <React.Fragment key={key}>
            <WeekLabel>{formatDate(getDateFromBlock(shifts[0].startBlock, timezoneName), 'EEE d')}</WeekLabel>
            {shifts.map(shift => (
              <UnpickedShiftRow key={shift.shiftId}>
                <UnpickedShiftHour>{formatShiftLabel(shift.startBlock, shift.endBlock)}</UnpickedShiftHour>
              </UnpickedShiftRow>
            ))}
          </React.Fragment>
        ))}
      </UnpickedShiftsContainer>

      <StyledButton onClick={onSubmit} color="primary">
        {t`common.btnOk`}
      </StyledButton>
    </ContainerBody>
  );
};

export { PickedShiftsWithWarning };

const ContainerBody = styled.div.attrs({
  className: `${flex.columnCenter} align-items-center pb-4 px-3`,
})`
  background-color: #f9fafb;
  border: 1px solid #f8f9fa;
  overflow: hidden auto;
  max-height: calc(
    90vh - 2 * ${SHIFTPICKUP_CART_HEADER_HEIGHT}px - ${NAVBAR_HEIGHT}px - ${SHIFTPICKUP_CART_TOP_SPACE}px
  );

  ${media.phone} {
    max-height: 400px;
  }
`;

const StyledIcon = styled.img`
  width: 37.33px;
  height: 48px;

  margin: 30px 0;
`;

const StyledButton = styled(Button)`
  width: 160px;
  margin: 20px 0;
`;

const WeekLabel = styled.div.attrs({
  className: 'pt-2 pb-1',
})`
  font-size: 12px;
  line-height: 18px;
  color: #5a5a5a;
`;

const UnpickedShiftsContainer = styled.div`
  width: 100%;
`;

const UnpickedShiftRow = styled.div.attrs({
  className: `${flex.rowCenter} justify-content-between pe-2`,
})`
  color: #4b5563;
`;

const UnpickedShiftHour = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
`;
